// App Success Stories - Real project results and case studies
import React, { useState, useEffect } from 'react';
import { TrendingUp, Clock, Smartphone, Users, Zap, Brain, CheckCircle, ChevronLeft, ChevronRight, Search, FileText, Target, Building2, Radio, Newspaper, Database, Activity, LucideIcon, Workflow, Layers, Cpu, Mic, GraduationCap, MapPin, PenTool, LineChart, Filter, MessageSquare, Briefcase, Inbox, Wallet } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import lhcDashboard from '@/assets/lhc-dashboard.png.asset.json';
import lhcContentLab from '@/assets/lhc-contentlab.png.asset.json';
import lhcKeywordMap from '@/assets/lhc-keywordmap.png.asset.json';
import securdiaDashboard from '@/assets/securdia-dashboard.png.asset.json';
import securdiaLead from '@/assets/securdia-lead.png.asset.json';
import movementDashboard from '@/assets/movement-dashboard.png.asset.json';

type Architecture = {
  pipeline?: { name: string; role: string; icon: LucideIcon }[];
  dataModel?: { entity: string; fields: string[] }[];
  realtime?: string;
};

const successStories: Array<{
  id: string;
  title: string;
  subtitle: string;
  description: string;
  timeline: string;
  category: string;
  results: { metric: string; label: string; icon: LucideIcon }[];
  techStack: string[];
  problem: string;
  approach: string;
  outcome: string;
  architecture?: Architecture;
  image: string;
  screenshots: string[];
  logo?: string;
}> = [{
  id: 'propertyagent-crm',
  title: 'PropertyAgent CRM',
  subtitle: 'A real estate CRM built around how agents actually work',
  description: 'A mobile-first CRM for solo agents and small brokerages. Visual pipeline, commission forecasting, and an AI copilot that flags deals at risk of slipping.',
  timeline: '12 weeks',
  category: 'Web Application + PWA',
  results: [{
    metric: 'Faster',
    label: 'Closes than spreadsheet pipelines',
    icon: TrendingUp
  }, {
    metric: 'Mobile-first',
    label: 'PWA for field work',
    icon: Smartphone
  }, {
    metric: 'AI Copilot',
    label: 'At-risk deal alerts',
    icon: Brain
  }, {
    metric: 'Kanban',
    label: 'Visual deal pipeline',
    icon: CheckCircle
  }],
  techStack: ['React', 'TypeScript', 'Supabase', 'PWA', 'Tailwind CSS'],
  problem: 'Agents were running their pipelines out of spreadsheets and generic CRMs that were never designed for property work. Commissions lived in their heads, follow-ups slipped between viewings, and nothing was usable from a phone in the car between showings.',
  approach: 'We rebuilt the CRM around a single working day in the life of an agent. A drag-and-drop Kanban for deals, commission forecasting that updates as stages move, and an AI copilot that watches activity gaps and quietly flags deals that have gone cold. The whole thing ships as a PWA so it installs to the home screen and works on patchy mobile networks.',
  outcome: 'Agents who switched to the platform stopped losing deals to forgotten follow-ups. The pipeline now reflects reality instead of being updated once a week, and forecasting that used to take a Sunday evening happens in the background.',
  architecture: {
    pipeline: [
      { name: 'Lead intake', role: 'Capture from web forms, referrals, and imports', icon: Inbox },
      { name: 'Deal pipeline', role: 'Drag-and-drop Kanban with stage automations', icon: Workflow },
      { name: 'Commission engine', role: 'Forecasts revenue as stages move', icon: Wallet },
      { name: 'AI copilot', role: 'Flags deals going cold based on activity gaps', icon: Brain }
    ],
    dataModel: [
      { entity: 'deals', fields: ['stage', 'value', 'expected_close', 'risk_score'] },
      { entity: 'contacts', fields: ['role', 'source', 'last_touch_at'] },
      { entity: 'commissions', fields: ['deal_id', 'split', 'forecast', 'realised'] }
    ],
    realtime: 'Optimistic updates on the Kanban so drag-and-drop feels instant, with Supabase syncing changes in the background. The PWA caches the active pipeline so reps can keep working in elevators, basements, and underground parking.'
  },
  image: '/images/portfolio/propertyagent-dashboard.png',
  screenshots: [
    '/images/portfolio/propertyagent-hero.png',
    '/images/portfolio/propertyagent-dashboard.png',
    '/images/portfolio/propertyagent-pipeline.png'
  ],
  logo: '/images/portfolio/propertyagent-logo.png'
}, {
  id: 'selgr',
  title: 'SELGR',
  subtitle: 'AI sales training for B2B teams',
  description: 'A practical training platform where salespeople rehearse real customer conversations with an AI that knows their product, their company, and their objections.',
  timeline: '16 weeks',
  category: 'SaaS Platform',
  results: [{
    metric: 'AI Roleplay',
    label: 'Realistic buyer simulations',
    icon: Brain
  }, {
    metric: 'Personal',
    label: 'Adapts to product and company',
    icon: Users
  }, {
    metric: '10+',
    label: 'Structured course modules',
    icon: GraduationCap
  }, {
    metric: 'Live',
    label: 'Coaching during the call',
    icon: Mic
  }],
  techStack: ['React', 'Node.js', 'PostgreSQL', 'OpenAI', 'Gemini', 'Auth0', 'Stripe'],
  problem: 'B2B teams were stuck choosing between expensive in-person coaching and theoretical e-learning that nobody finished. Reps wanted a safe place to actually practise a discovery call or handle an objection, with feedback they could use the next morning.',
  approach: 'We built a training platform around the conversation, not the curriculum. Each rep configures their product, ICP, and tone of voice once, then opens a roleplay against an AI buyer that pushes back the way real prospects do. Live coaching surfaces during the call, and structured modules wrap around the practice so progression feels deliberate, not random.',
  outcome: 'Teams use SELGR as their warm-up before real calls. Managers see consistent gains in objection handling and discovery depth across the team, and reps log in voluntarily, which is rare for sales training.',
  architecture: {
    pipeline: [
      { name: 'Course engine', role: 'Modules, lessons, quizzes, and progression', icon: GraduationCap },
      { name: 'AI roleplay', role: 'Personas that react to tone, push back, and stall', icon: Mic },
      { name: 'Coaching layer', role: 'Live feedback and post-call scoring', icon: Brain },
      { name: 'Progress tracker', role: 'XP, badges, and skill heatmap per rep', icon: TrendingUp }
    ],
    dataModel: [
      { entity: 'users', fields: ['company_profile', 'product_context', 'level'] },
      { entity: 'modules', fields: ['topic', 'lessons', 'difficulty'] },
      { entity: 'sessions', fields: ['persona', 'transcript', 'scores'] },
      { entity: 'progress', fields: ['xp', 'badges', 'skill_scores'] }
    ],
    realtime: 'Roleplay transcripts and coaching feedback stream over a websocket so the rep sees suggestions appear as they speak. Sessions are persisted incrementally, so a dropped connection never loses a practice run.'
  },
  image: '/images/portfolio/selgr-roleplay.png',
  screenshots: [
    '/images/portfolio/selgr-hero.png',
    '/images/portfolio/selgr-course.png',
    '/images/portfolio/selgr-roleplay.png'
  ],
  logo: '/images/portfolio/selgr-logo.png'
}, {
  id: 'lhseo',
  title: 'LHC SEO Center',
  subtitle: 'In-house content engine for Light House Company',
  description: 'A private SEO workspace that turns keyword research into ranked, on-brand articles. Light House Company runs the full content pipeline from one screen.',
  timeline: '8 weeks',
  category: 'AI Content Platform',
  results: [{
    metric: '~6 min',
    label: 'From keyword to draft',
    icon: Zap
  }, {
    metric: '24',
    label: 'Keywords tracked daily',
    icon: Search
  }, {
    metric: '9',
    label: 'Articles shipped in month one',
    icon: FileText
  }, {
    metric: 'Avg #10',
    label: 'Google position across set',
    icon: TrendingUp
  }],
  techStack: ['React', 'TypeScript', 'Supabase', 'Edge Functions', 'Gemini 2.5 Pro', 'Tailwind CSS'],
  problem: 'Light House Company wanted to own their SEO without handing it to an agency or flooding the site with generic AI text. The team writes like lighting specialists: specific, technical, grounded in real product standards. Off-the-shelf tools could not match that voice.',
  approach: 'A focused three-step workspace. Keyword Map tracks the strategic terms and their daily Google position. Content Lab drafts each article through Gemini 2.5 Pro with a tightly constrained prompt that locks tone, audience, and source material. Editorial guardrails block filler phrases and made-up statistics before the draft ever reaches a human.',
  outcome: 'The team ships professional articles in roughly six minutes from keyword to draft and shipped nine in the first month. Average rank across the tracked set sits at position 10, with the strongest pieces breaking into the top three.',
  architecture: {
    pipeline: [
      { name: 'Keyword Map', role: 'Strategic terms, priorities, and categories', icon: MapPin },
      { name: 'Content Lab', role: 'Gemini 2.5 Pro drafts with editorial guardrails', icon: PenTool },
      { name: 'Rank tracker', role: 'Daily Google position checks against lhc.no', icon: LineChart }
    ],
    dataModel: [
      { entity: 'keywords', fields: ['term', 'priority', 'category', 'target_audience'] },
      { entity: 'articles', fields: ['keyword_id', 'h1', 'meta', 'body_md', 'status'] },
      { entity: 'rank_checks', fields: ['keyword_id', 'position', 'checked_at'] }
    ],
    realtime: 'Rank checks run on a scheduled edge function once per day and write back to the dashboard. Generation itself is request-response, so writers see drafts appear within minutes rather than waiting for batch jobs.'
  },
  image: lhcDashboard.url,
  screenshots: [
    lhcDashboard.url,
    lhcContentLab.url,
    lhcKeywordMap.url
  ]
}, {
  id: 'securdia-prospecting',
  title: 'Securdia Prospecting Platform',
  subtitle: 'B2B sales intelligence for the care sector',
  description: 'A focused prospecting workspace for Securdia. It pulls company data straight from the national business registry, scores every lead against the care market, and surfaces the right municipalities and private operators to call next.',
  timeline: '10 weeks',
  category: 'Web Application',
  results: [{
    metric: 'Ranked',
    label: 'Lead list generated nightly',
    icon: Target
  }, {
    metric: 'Tender',
    label: 'Public tender monitoring',
    icon: FileText
  }, {
    metric: 'Registry',
    label: 'Nightly company enrichment',
    icon: Building2
  }, {
    metric: 'AI Scoring',
    label: 'HOT/WARM/MONITOR segmentation',
    icon: Brain
  }],
  techStack: ['React', 'TypeScript', 'Supabase', 'AI', 'Web Scraping'],
  problem: 'Securdia sells into a fragmented market of municipalities, day centres, and private care providers. The sales team was burning mornings on registry exports and public tender PDFs trying to find the few accounts that actually fit, with no shared system for tracking who had been called or why a lead mattered.',
  approach: 'We turned raw public registry data into a ranked call list. A nightly registry sync enriches every company. A tender scraper watches for relevant public tenders. A scoring engine grades each lead across six dimensions and segments them into HOT, WARM, and MONITOR. AI talking points are generated per lead so the rep opens the dashboard knowing exactly what to say.',
  outcome: 'Reps now start the day with a short, ranked list of accounts that actually fit the care market. Time spent on manual research collapsed, and the pipeline reflects real fit instead of whoever happened to be in the last export.',
  architecture: {
    pipeline: [
      { name: 'Registry sync', role: 'Nightly enrichment of company records', icon: Building2 },
      { name: 'Tender scraper', role: 'Firecrawl-driven public tender monitoring', icon: Search },
      { name: 'Lead scorer', role: 'Six-dimension score, HOT/WARM/MONITOR tiering', icon: Target },
      { name: 'AI talking points', role: 'Per-lead arguments grounded in context', icon: MessageSquare }
    ],
    dataModel: [
      { entity: 'companies', fields: ['org_number', 'nace', 'size', 'segment'] },
      { entity: 'leads', fields: ['company_id', 'score', 'tier', 'status'] },
      { entity: 'calls', fields: ['lead_id', 'outcome', 'notes', 'follow_up_at'] },
      { entity: 'tenders', fields: ['source', 'value', 'deadline', 'matched_leads'] }
    ],
    realtime: 'Edge functions run scrapers and scoring on a cron, then push invalidations through TanStack Query so dashboards refresh as soon as new leads land. No manual refresh, no stale lists.'
  },
  image: securdiaDashboard.url,
  screenshots: [
    securdiaDashboard.url,
    securdiaLead.url
  ]
}, {
  id: 'movement-opportunity-engine',
  title: 'Movement Opportunity Engine',
  subtitle: 'Early-signal prospecting for an office furniture brand',
  description: 'A prospecting platform that spots companies about to buy office furniture before they start looking. One dashboard, three scanners, signals ranked by strength.',
  timeline: '9 weeks',
  category: 'B2B Signal Intelligence',
  results: [{
    metric: '3 engines',
    label: 'News, capital, and tenders',
    icon: Radio
  }, {
    metric: '0–100',
    label: 'Signal strength per lead',
    icon: Target
  }, {
    metric: 'Realtime',
    label: 'New tenders pushed live',
    icon: Zap
  }, {
    metric: 'Daily',
    label: 'Auto-scan at 08:00',
    icon: Newspaper
  }],
  techStack: ['React', 'TypeScript', 'Supabase', 'Edge Functions', 'Postgres Realtime', 'Tailwind CSS', 'shadcn/ui'],
  problem: 'Movement found out about office moves and new headquarters the same way everyone else did: too late. By the time a relocation hit LinkedIn, the furniture decision was already made. The team needed the upstream signals, lease announcements, capital raises, public tenders, without spending mornings reading the business press.',
  approach: 'Three independent scanning engines run on a schedule and feed one Kanban dashboard. The news scanner crawls real estate and business media, classifying signals into lease, move, expansion, and new-build. Capital radar pulls newly registered companies from public business registries with meaningful share capital. The public tender scanner watches for furniture-related tenders. Everything is scored from 0 to 100 so reps work the strongest signal first.',
  outcome: 'Movement reps stopped chasing news manually and started every morning with a scored feed of real signals. Deals that used to be found by luck are now surfaced systematically, and the team can defend which accounts they are working and why.',
  architecture: {
    pipeline: [
      { name: 'news-scanner', role: 'Classifies real estate media into lease, move, expansion, new-build', icon: Newspaper },
      { name: 'capital-radar', role: 'New companies with meaningful share capital', icon: Wallet },
      { name: 'doffin-scanner', role: 'Furniture-related public tenders, daily', icon: Briefcase },
      { name: 'Signal scorer', role: 'Strength score 0–100 across all sources', icon: Target }
    ],
    dataModel: [
      { entity: 'opportunities', fields: ['company_name', 'org_number', 'signal_type', 'signal_strength', 'status'] },
      { entity: 'opportunities (signals)', fields: ['NEWS_LEASE', 'NEWS_MOVE', 'NEWS_EXPANSION', 'NEWS_NEWBUILD', 'HIGH_CAPITAL', 'PUBLIC_TENDER'] },
      { entity: 'opportunities (status flow)', fields: ['ny', 'kontaktet', 'kvalifisert', 'tilbud_sendt', 'vunnet', 'tapt', 'ikke_relevant'] }
    ],
    realtime: 'The dashboard subscribes to Supabase postgres_changes on INSERT for PUBLIC_TENDER, so new tenders trigger toast notifications and an auto-refresh the moment they land in the database. Expired leads past their move or deadline date filter out client-side, and source URLs are validated before a card ever renders.'
  },
  image: movementDashboard.url,
  screenshots: [
    movementDashboard.url
  ]
}];

export const AppSuccessStories = () => {
  const [activeStory, setActiveStory] = useState(0);
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const { elementRef, getRevealStyle } = useScrollReveal();
  
  const currentStory = successStories[activeStory];
  const screenshots = currentStory.screenshots || [currentStory.image];
  
  // Reset screenshot index when switching stories
  const handleStoryChange = (index: number) => {
    setActiveStory(index);
    setActiveScreenshot(0);
    setProgress(0);
  };
  
  // Auto-play with progress tracking
  useEffect(() => {
    if (isPaused || screenshots.length <= 1) {
      return;
    }
    
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setActiveScreenshot((current) => (current + 1) % screenshots.length);
          return 0;
        }
        return prev + 2; // 2% every 80ms = 4 seconds total
      });
    }, 80);
    
    return () => clearInterval(progressInterval);
  }, [isPaused, screenshots.length, activeStory]);
  
  // Reset progress when manually changing screenshot
  const goToScreenshot = (index: number) => {
    setActiveScreenshot(index);
    setProgress(0);
  };
  
  const nextScreenshot = () => {
    setActiveScreenshot((prev) => (prev + 1) % screenshots.length);
    setProgress(0);
  };
  
  const prevScreenshot = () => {
    setActiveScreenshot((prev) => (prev - 1 + screenshots.length) % screenshots.length);
    setProgress(0);
  };

  return (
    <section className="section">
      <div ref={elementRef} className="container-professional">
        {/* Header */}
        <div className="section-header-professional">
          <div className="micro-label-professional">SUCCESS STORIES</div>
          <h2 className="heading-section">
            Real projects, measurable results
          </h2>
          <p className="text-body max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Discover how we've helped businesses streamline operations and achieve growth goals
          </p>
        </div>

        {/* Story navigation tabs - Premium design */}
        <div className="flex flex-wrap justify-center gap-4 mb-12 max-w-4xl mx-auto">
          {successStories.map((story, index) => (
            <button
              key={story.id}
              onClick={() => handleStoryChange(index)}
              className={`
                relative px-6 py-4 rounded-xl text-left transition-all duration-300
                border backdrop-blur-sm
                ${activeStory === index 
                  ? 'bg-gradient-to-br from-primary/20 to-primary/5 border-primary/50 shadow-lg shadow-primary/10' 
                  : 'bg-[#0d0d0d] border-white/10 hover:border-white/20 hover:bg-[#111111]'
                }
              `}
            >
              <div className="flex items-center gap-3">
                {story.logo && (
                  <div className="bg-white rounded-lg p-1.5 flex-shrink-0">
                    <img src={story.logo} alt="" className="h-6 w-auto" />
                  </div>
                )}
                <div>
                  <div className={`font-semibold text-sm ${activeStory === index ? 'text-white' : 'text-white/80'}`}>
                    {story.title}
                  </div>
                  <div className={`text-xs mt-0.5 ${activeStory === index ? 'text-primary' : 'text-white/75'}`}>
                    {story.category}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Active story content */}
        <div className="bg-[#111111] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 md:p-12 max-w-5xl mx-auto" style={getRevealStyle(1)}>
          {/* Logo and Title */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              {currentStory.logo && (
                <div className="bg-white rounded-lg p-3 flex-shrink-0">
                  <img src={currentStory.logo} alt={`${currentStory.title} logo`} className="h-8 w-auto" />
                </div>
              )}
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary/20 text-primary">
                  {currentStory.category}
                </span>
                <span className="flex items-center gap-2 text-xs text-white/75">
                  <Clock className="w-3.5 h-3.5" />
                  {currentStory.timeline}
                </span>
              </div>
            </div>
            
            <h3 className="text-2xl font-semibold text-white mb-3 leading-tight">
              {currentStory.title}
            </h3>
            
            <p className="text-base leading-relaxed text-white/70">
              {currentStory.description}
            </p>
          </div>

          {/* Screenshot Carousel - Premium Browser Frame */}
          {screenshots.length > 0 && (
            <div className="mb-10 -mx-8 md:-mx-12">
              <div 
                className="relative group"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                {/* Browser Frame Container */}
                <div className="bg-[#1a1a1a] rounded-xl overflow-hidden border border-white/10 shadow-2xl transition-all duration-500 hover:shadow-primary/5">
                  {/* Browser Bar */}
                  <div className="flex items-center gap-3 px-4 py-3 bg-[#0d0d0d] border-b border-white/5">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                      <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
                      <div className="w-3 h-3 rounded-full bg-[#28ca42]" />
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-[#0a0a0a] rounded-md px-4 py-1.5 text-xs text-white/70 text-center max-w-xs mx-auto border border-white/5">
                        {currentStory.title.toLowerCase().replace(/\s+/g, '')}.app
                      </div>
                    </div>
                    <div className="w-16" /> {/* Spacer for symmetry */}
                  </div>
                  
                  {/* Screenshot Image */}
                  <div className="relative overflow-hidden">
                    <img 
                      key={`${activeStory}-${activeScreenshot}`}
                      src={screenshots[activeScreenshot]} 
                      alt={`${currentStory.title} interface ${activeScreenshot + 1}`} 
                      className="w-full h-auto animate-fade-in" 
                    />
                  </div>
                </div>
                
                {/* Navigation Arrows - Premium Style */}
                {screenshots.length > 1 && (
                  <>
                    <button
                      onClick={prevScreenshot}
                      className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full 
                        bg-gradient-to-r from-[#0a0a0a] to-[#0a0a0a]/80 
                        backdrop-blur-md border border-white/20 
                        flex items-center justify-center 
                        opacity-0 group-hover:opacity-100 
                        hover:border-primary hover:scale-110 hover:shadow-lg hover:shadow-primary/20
                        transition-all duration-300"
                      aria-label="Previous screenshot"
                    >
                      <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                    <button
                      onClick={nextScreenshot}
                      className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full 
                        bg-gradient-to-r from-[#0a0a0a]/80 to-[#0a0a0a] 
                        backdrop-blur-md border border-white/20 
                        flex items-center justify-center 
                        opacity-0 group-hover:opacity-100 
                        hover:border-primary hover:scale-110 hover:shadow-lg hover:shadow-primary/20
                        transition-all duration-300"
                      aria-label="Next screenshot"
                    >
                      <ChevronRight className="w-6 h-6 text-white" />
                    </button>
                  </>
                )}
              </div>
              
              {/* Premium Progress Bar Indicators */}
              {screenshots.length > 1 && (
                <div className="flex gap-2 mt-6 px-8 md:px-12">
                  {screenshots.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToScreenshot(index)}
                      className="h-1 flex-1 rounded-full overflow-hidden bg-white/10 hover:bg-white/20 transition-colors"
                      aria-label={`View screenshot ${index + 1}`}
                    >
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-100 ease-linear"
                        style={{
                          width: activeScreenshot === index 
                            ? `${progress}%` 
                            : activeScreenshot > index 
                              ? '100%' 
                              : '0%'
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Results metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {currentStory.results.map((result, index) => {
              const IconComponent = result.icon;
              return (
                <div key={index} className="text-center p-4 bg-[#0a0a0a] rounded-xl border border-white/8 hover:border-white/15 transition-colors">
                  <IconComponent className="w-6 h-6 mx-auto mb-2 text-primary" strokeWidth={1.5} />
                  <div className="text-xl font-bold text-white mb-1">{result.metric}</div>
                  <div className="text-xs text-white/75">{result.label}</div>
                </div>
              );
            })}
          </div>

          {/* Narrative: problem → approach → outcome */}
          <div className="space-y-8 mb-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-primary/80 font-semibold mb-3">The problem</div>
              <p className="text-base leading-relaxed text-white/75">
                {currentStory.problem}
              </p>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-primary/80 font-semibold mb-3">The approach</div>
              <p className="text-base leading-relaxed text-white/75">
                {currentStory.approach}
              </p>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-primary/80 font-semibold mb-3">The outcome</div>
              <p className="text-base leading-relaxed text-white/75">
                {currentStory.outcome}
              </p>
            </div>
          </div>

          {/* Technical architecture */}
          {currentStory.architecture && (
            <div className="mb-10 p-6 md:p-8 rounded-xl bg-[#0a0a0a] border border-white/8">
              <div className="text-[11px] uppercase tracking-[0.18em] text-primary/80 font-semibold mb-5 flex items-center gap-2">
                <Cpu className="w-3.5 h-3.5" strokeWidth={2} />
                Technical architecture
              </div>

              {currentStory.architecture.pipeline && (
                <div className="mb-7">
                  <div className="flex items-center gap-2 text-xs font-semibold text-white/90 mb-3">
                    <Workflow className="w-3.5 h-3.5 text-primary" strokeWidth={1.8} />
                    Pipeline
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {currentStory.architecture.pipeline.map((step, idx) => {
                      const Icon = step.icon;
                      return (
                        <div key={idx} className="p-3 rounded-lg bg-white/[0.03] border border-white/8">
                          <div className="flex items-center gap-2 mb-1.5">
                            <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
                            <span className="text-xs font-semibold text-white">{step.name}</span>
                          </div>
                          <p className="text-[11px] leading-relaxed text-white/75">{step.role}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {currentStory.architecture.dataModel && (
                <div className="mb-7">
                  <div className="flex items-center gap-2 text-xs font-semibold text-white/90 mb-3">
                    <Database className="w-3.5 h-3.5 text-primary" strokeWidth={1.8} />
                    Data model
                  </div>
                  <div className="space-y-2">
                    {currentStory.architecture.dataModel.map((row, idx) => (
                      <div key={idx} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/8">
                        <code className="text-[11px] font-mono text-primary whitespace-nowrap">{row.entity}</code>
                        <div className="flex flex-wrap gap-1.5">
                          {row.fields.map((field, fi) => (
                            <span key={fi} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-white/75 border border-white/8">
                              {field}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {currentStory.architecture.realtime && (
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-white/90 mb-3">
                    <Activity className="w-3.5 h-3.5 text-primary" strokeWidth={1.8} />
                    Realtime layer
                  </div>
                  <p className="text-sm leading-relaxed text-white/65">
                    {currentStory.architecture.realtime}
                  </p>
                </div>
              )}
            </div>
          )}


          {/* Tech Stack */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-4">Technology Stack</h4>
            <div className="flex flex-wrap gap-2">
              {currentStory.techStack.map((tech, index) => (
                <span 
                  key={index} 
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border bg-white/5 text-white/70 border-white/8"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
